/**
 * 💾 **화면 보관함** — 웹에 올리지 않고 **하던 일만** 저장한다(모든 화면 공용).
 *
 * ## 왜 (2026-08-22 요청)
 * 저장하는 길이 화면마다 [웹에 올리기] **하나뿐**이었다. 그래서
 *  · 중간에 저장하면 **미완성이 아이 화면에 나가고**
 *  · 안 하면 창을 닫는 순간 다 날아간다.
 * 전자책만 이 문제를 먼저 겪어서 거기만 보관함이 있었는데(`bookDraft`), 굿즈·행사·랠리·
 * 소식·배치도 똑같이 오래 걸리는 일이다. **화면마다 다시 짜지 않도록** 여기로 모았다.
 *
 * ## 이 기기에만 남는다
 * 서버에 「미완성 보관용 표」를 새로 만들지 않았다 — 작업 중인 원고는 그 사람 것이고,
 * 다른 기기에서 이어 하는 일은 드물다. 대신 화면에 **그렇게 적는다**. 안 적으면 다른 PC 에서
 * 찾다가 잃어버린 줄 안다.
 *
 * ## 그림은 이미 서버에 있다
 * 그림·소리는 **올리는 순간 엣지에 저장된다**(게시와 별개). 보관함에는 주소만 담긴다 —
 * 큰 파일을 브라우저에 쟁이지 않는다. 임시 `data:` 주소는 아예 뺀다(저장소가 금방 찬다).
 */

const KEY = (id: string) => `gc_admin_draft_${id}`;

export interface ScreenDraft<T = unknown> {
  data: T;
  at: number;
}

/**
 * `data:` 주소를 통째로 걷어낸다.
 *
 * 서버가 잠깐 안 붙었을 때 올린 그림은 `data:image/...` 로 화면에만 들고 있는다. 그걸
 * 보관함에 넣으면 한 장에 수 MB 라 **저장소가 한 번에 찬다**(그 뒤로는 아무것도 저장 안 됨).
 * 어차피 그런 그림은 게시도 못 하므로 담을 이유가 없다.
 */
function stripDataUrls(v: unknown, depth = 0): unknown {
  if (depth > 12) return undefined; // 순환·과도한 중첩 방어(보관은 부가 기능이다)
  if (typeof v === "string") return v.startsWith("data:") ? undefined : v;
  if (Array.isArray(v)) return v.map((x) => stripDataUrls(x, depth + 1));
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      const s = stripDataUrls(x, depth + 1);
      if (s !== undefined) out[k] = s;
    }
    return out;
  }
  return v;
}

/**
 * 저장. **실패해도 던지지 않는다** — 보관은 부가 기능이고, 편집을 막으면 본말이 뒤집힌다.
 * 대신 false 를 돌려주니 호출부가 "저장 못 했어요"를 말할 수 있다(조용히 삼키지 않는다).
 */
export function saveScreenDraft(id: string, data: unknown): boolean {
  return draftSaveError(id, data) === null;
}

/**
 * 저장하고 **왜 안 됐는지**를 사람 말로 돌려준다(성공이면 `null`).
 *
 * 🔴 예전엔 참/거짓만 돌려줬고, 저장바는 화면 이름만 적었다 — 「⚠️ 저장 못 함: 읽어주기」.
 * 운영자도 이걸 고치러 온 사람도 왜인지 알 수가 없다. 저장소가 꽉 찬 건지, 사생활 보호
 * 모드인지, 코드가 터진 건지. 실제로 그 한 줄이 배선 버그를 몇 판 동안 가리고 있었다
 * (2026-08-23 — [💾 저장하기]가 17개 화면 전부에서 안 눌리고 있었다).
 */
export function draftSaveError(id: string, data: unknown): string | null {
  if (!id) return "저장할 자리 이름이 없어요";
  let json: string;
  try {
    json = JSON.stringify({ data: stripDataUrls(data), at: Date.now() });
  } catch (e) {
    return `보관할 값을 만들지 못했어요(${(e as Error).message.slice(0, 60)})`;
  }
  try {
    localStorage.setItem(KEY(id), json);
    return null;
  } catch (e) {
    // 꽉 찼는지 · 막혔는지를 갈라서 말한다. 할 일이 다르다(지우기 ↔ 창을 바꾸기).
    const name = (e as { name?: string })?.name ?? "";
    const kb = Math.ceil(json.length / 1024);
    if (name === "QuotaExceededError" || /quota/i.test(String((e as Error)?.message))) {
      return `브라우저 저장소가 꽉 찼어요(넣으려던 것 ${kb}KB). 다른 화면의 저장해 둔 작업을 버려 주세요`;
    }
    return `브라우저가 저장을 막았어요 — 사생활 보호(시크릿) 창인지 확인해 주세요`;
  }
}

export function loadScreenDraft<T = unknown>(id: string): ScreenDraft<T> | null {
  try {
    const raw = localStorage.getItem(KEY(id));
    if (!raw) return null;
    const d = JSON.parse(raw) as ScreenDraft<T>;
    return d && typeof d === "object" && "data" in d ? d : null;
  } catch {
    return null; // 깨진 값 — 없는 것으로
  }
}

export function clearScreenDraft(id: string): void {
  try {
    localStorage.removeItem(KEY(id));
  } catch {
    /* 무시 — 지우기 실패로 편집을 막지 않는다 */
  }
}

/** "3분 전"처럼 사람이 읽는 시각 — 언제 저장한 건지 안 보이면 불러올지 말지 못 정한다. */
export function draftAgeText(at: number, now = Date.now()): string {
  const m = Math.floor((now - at) / 60000);
  if (m < 1) return "방금";
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  return `${Math.floor(h / 24)}일 전`;
}
